import { useState, useCallback } from 'react'
import { updateCoursePublicationStatus } from '../../utils/api'

type GuardedAction = () => Promise<void> | void

type UsePublishGuardProps = {
  courseID: string | undefined
  onUnpublishSuccess: () => void
  showError: (msg: string) => void
}

export function usePublishGuard({
  courseID,
  onUnpublishSuccess,
  showError,
}: UsePublishGuardProps) {
  let [pendingAction, setPendingAction] = useState<GuardedAction | null>(null)
  let [dialogMessage, setDialogMessage] = useState('')
  let [isUnpublishing, setIsUnpublishing] = useState(false)

  let dialogOpen = pendingAction !== null

  let triggerGuard = useCallback(
    (wouldViolate: boolean, message: string, action: GuardedAction) => {
      if (!wouldViolate) {
        Promise.resolve()
          .then(action)
          .catch(() => showError('Action failed unexpectedly'))
        return
      }
      setDialogMessage(message)
      // Wrap in arrow to avoid useState treating it as an updater fn
      setPendingAction(() => action)
    },
    [showError],
  )

  let handleConfirm = useCallback(async () => {
    if (!courseID || !pendingAction) return
    setIsUnpublishing(true)
    try {
      await updateCoursePublicationStatus(courseID, 'unpublished')
    } catch {
      showError('Failed to unpublish course')
      setIsUnpublishing(false)
      setPendingAction(null)
      setDialogMessage('')
      return
    }
    try {
      onUnpublishSuccess()
      await Promise.resolve().then(pendingAction)
    } catch {
      showError('Action failed after unpublishing')
    } finally {
      setIsUnpublishing(false)
      setPendingAction(null)
      setDialogMessage('')
    }
  }, [courseID, pendingAction, onUnpublishSuccess, showError])

  let handleCancel = useCallback(() => {
    setPendingAction(null)
    setDialogMessage('')
  }, [])

  return {
    dialogOpen,
    dialogMessage,
    isUnpublishing,
    triggerGuard,
    handleConfirm,
    handleCancel,
  }
}
